import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type TestimonialStatus = "pending" | "approved" | "published" | "rejected" | "archived";

const STATUSES: TestimonialStatus[] = ["pending", "approved", "published", "rejected", "archived"];

type Row = {
  id: string;
  full_name: string;
  email: string;
  organization: string | null;
  role_title: string | null;
  quote: string;
  status: TestimonialStatus;
  moderator_notes: string | null;
  consent_publish_quote: boolean;
  signature_name: string | null;
  signed_at: string | null;
  release_effective_date: string | null;
  release_expires_at: string | null;
  published_at: string | null;
  moderated_at: string | null;
  created_at: string;
};

const STATUS_TONE: Record<TestimonialStatus, string> = {
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  approved: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  published: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
  archived: "bg-muted text-muted-foreground border-border",
};

const today = () => new Date().toISOString().slice(0, 10);

const fmt = (value: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

type WindowState = "active" | "scheduled" | "expired" | "incomplete";

function windowState(row: Row): WindowState {
  if (!row.release_effective_date) return "incomplete";
  const now = today();
  if (row.release_expires_at && row.release_expires_at < row.release_effective_date) return "incomplete";
  if (row.release_effective_date > now) return "scheduled";
  if (row.release_expires_at && row.release_expires_at < now) return "expired";
  return "active";
}

const WINDOW_LABEL: Record<WindowState, string> = {
  active: "Window active",
  scheduled: "Scheduled",
  expired: "Expired",
  incomplete: "Incomplete dates",
};

/** Matches enforce_testimonial_release(): these must pass before status can become published. */
function publishBlockers(row: Row): string[] {
  const missing: string[] = [];
  if (!row.consent_publish_quote) missing.push("quote consent");
  if (!row.signature_name || row.signature_name.trim().length < 2) missing.push("signature");
  if (!row.signed_at) missing.push("signed timestamp");
  if (!row.release_effective_date) missing.push("effective date");
  if (
    row.release_expires_at &&
    row.release_effective_date &&
    row.release_expires_at < row.release_effective_date
  ) {
    missing.push("valid release window");
  }
  return missing;
}

const AdminTestimonialReleases = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const [statusFilter, setStatusFilter] = useState<"all" | TestimonialStatus>("all");
  const [windowFilter, setWindowFilter] = useState<"all" | WindowState>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .rpc("has_role", { _user_id: user.id, _role: "admin" })
      .then(({ data, error }) => setIsAdmin(!error && data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("testimonial_submissions")
      .select(
        "id,full_name,email,organization,role_title,quote,status,moderator_notes,consent_publish_quote,signature_name,signed_at,release_effective_date,release_expires_at,published_at,moderated_at,created_at",
      )
      .order("created_at", { ascending: false })
      .limit(300);
    setLoading(false);
    if (error) {
      toast.error("Could not load testimonials", { description: error.message });
      return;
    }
    setRows((data ?? []) as Row[]);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const setStatus = async (row: Row, next: TestimonialStatus) => {
    if (next === row.status) return;
    if (next === "published") {
      const missing = publishBlockers(row);
      if (missing.length) {
        toast.error("Cannot publish", { description: `Missing: ${missing.join(", ")}` });
        return;
      }
    }
    setBusyId(row.id);
    const { data, error } = await supabase.rpc("set_testimonial_release_status", {
      _id: row.id,
      _status: next,
      _notes: notes[row.id]?.trim() ? notes[row.id].trim() : null,
    });
    setBusyId(null);
    if (error) {
      toast.error("Status change failed", { description: error.message });
      return;
    }
    const updated = (Array.isArray(data) ? data[0] : data) as Row | null;
    if (updated) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
    } else {
      void load();
    }
    setNotes((prev) => ({ ...prev, [row.id]: "" }));
    toast.success(`Status set to ${next}`);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (windowFilter !== "all" && windowState(r) !== windowFilter) return false;
      if (!q) return true;
      return [r.full_name, r.email, r.organization ?? "", r.role_title ?? "", r.quote]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [query, rows, statusFilter, windowFilter]);

  if (authLoading || isAdmin === null) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with an administrator account to manage testimonial release status.
        </p>
        <Button asChild>
          <Link to="/auth">Sign in</Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <Helmet>
        <title>Testimonial Release Status | Admin</title>
        <meta name="description" content="Filter testimonials and toggle their release status." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Release status console</h1>
          <p className="text-sm text-muted-foreground">
            {visible.length} of {rows.length} testimonials shown.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/testimonials">Moderation</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/testimonials/audit">Audit log</Link>
          </Button>
        </div>
      </header>

      <Card className="mb-6">
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="release-search">Search</Label>
            <Input
              id="release-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, email, org, quote"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="release-status">Status</Label>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger id="release-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="release-window">Release window</Label>
            <Select value={windowFilter} onValueChange={(v) => setWindowFilter(v as typeof windowFilter)}>
              <SelectTrigger id="release-window">
                <SelectValue placeholder="All windows" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All windows</SelectItem>
                <SelectItem value="active">Window active</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="incomplete">Incomplete dates</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {visible.length === 0 ? (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No testimonials match these filters.
        </p>
      ) : (
        <ul className="space-y-4">
          {visible.map((row) => {
            const missing = publishBlockers(row);
            const ws = windowState(row);
            return (
              <li key={row.id}>
                <Card>
                  <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                    <div>
                      <CardTitle className="text-base">{row.full_name}</CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {[row.role_title, row.organization].filter(Boolean).join(" · ") || row.email}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={STATUS_TONE[row.status]}>
                        {row.status}
                      </Badge>
                      <Badge variant="outline">{WINDOW_LABEL[ws]}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <blockquote className="border-l-2 pl-3 text-sm text-muted-foreground">
                      “{row.quote}”
                    </blockquote>

                    <dl className="grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
                      <div>
                        <dt className="inline font-medium text-foreground">Effective: </dt>
                        <dd className="inline">{row.release_effective_date ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="inline font-medium text-foreground">Expires: </dt>
                        <dd className="inline">{row.release_expires_at ?? "no expiry"}</dd>
                      </div>
                      <div>
                        <dt className="inline font-medium text-foreground">Signed: </dt>
                        <dd className="inline">{fmt(row.signed_at)}</dd>
                      </div>
                      <div>
                        <dt className="inline font-medium text-foreground">Published: </dt>
                        <dd className="inline">{fmt(row.published_at)}</dd>
                      </div>
                    </dl>

                    {missing.length > 0 && (
                      <p className="text-xs text-destructive">Missing for publish: {missing.join(", ")}</p>
                    )}

                    {row.moderator_notes && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Latest note:</span> {row.moderator_notes}
                      </p>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor={`note-${row.id}`} className="text-xs">
                        Moderator note (saved with the next status change)
                      </Label>
                      <Textarea
                        id={`note-${row.id}`}
                        rows={2}
                        value={notes[row.id] ?? ""}
                        onChange={(e) => setNotes((prev) => ({ ...prev, [row.id]: e.target.value }))}
                        placeholder="Optional context for the audit log"
                      />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => {
                        const blocked = s === "published" && missing.length > 0;
                        return (
                          <Button
                            key={s}
                            size="sm"
                            variant={row.status === s ? "default" : "outline"}
                            disabled={busyId === row.id || row.status === s || blocked}
                            title={blocked ? `Missing: ${missing.join(", ")}` : undefined}
                            onClick={() => void setStatus(row, s)}
                          >
                            {busyId === row.id ? (
                              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                            ) : null}
                            {s}
                          </Button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
};

export default AdminTestimonialReleases;
